import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomPassword from "../../components/CustomInput/CustomPassword";
import FloatingLogo from "../../components/FloatingLogo/FloatingLogo";
import { ProgressSpinner } from "primereact/progressspinner";
import { H2, DangerText, SuccessH2, MediumText } from "../../styles/Typography";
import { Column, Container, InputFlex } from "./ActivateUser.styles";
import { IconMail } from "../../assets/icons/Icon";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

interface Password {
  password: string;
  confirm: string;
}

const ActivateUser: React.FC = () => {
  const { usersEP, handleActivateUser, toastRef } = useUsersAPI();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const userId = searchParams.get("user");
  const [isActivated, setIsActivated] = useState<boolean>(false);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<Password>({
    password: "",
    confirm: "",
  });
  const [touched, setTouched] = useState({ password: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const { data: user, isLoading } = useGetQuery(
    `${usersEP}/activate?userId=${userId}&token=${token}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !token || !userId || !usersEP,
    }
  );

  useEffect(() => {
    if (user) setEmail(user.email);
  }, [user]);

  const handleNewPasswordChange = (value: string) => {
    setPassword((prev) => ({ ...prev, password: value }));
  };

  const handleConfirmPasswordChange = (value: string) => {
    setPassword((prev) => ({ ...prev, confirm: value }));
  };

  const handleBlur = (field: "password" | "confirm") => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getPasswordError = () => {
    if (!touched.password) return "";
    if (password.password === "") return "";
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?]).{8,}$/;
    if (!passwordRegex.test(password.password)) {
      return "Пароль должен содержать минимум 8 символов, включая прописные буквы, цифры и специальные символы";
    }
    return "";
  };

  const getConfirmError = () => {
    if (!touched.confirm) return "";
    if (password.confirm === "") return "";
    if (!password.confirm) return "Подтвердите пароль";
    if (password.password !== password.confirm) return "Пароли не совпадают";
    return "";
  };

  const handleSubmit = async () => {
    setTouched({ password: true, confirm: true });
    const passwordError = getPasswordError();
    const confirmError = getConfirmError();
    if (passwordError || confirmError) return;

    setLoading(true);
    let success = await handleActivateUser(password.password, token!, userId!);
    if (success) {
      setLoading(false);
      setIsActivated(true);
    }
    setLoading(false);
  };

  if (isLoading) {
    <Container>
      <Spinner>
        <ProgressSpinner />
        <p>Загрузка...</p>
      </Spinner>
    </Container>;
  }

  if (!token || !userId) {
    return (
      <Column>
        <Container>
          <FloatingLogo />
          <H2 style={{ textAlign: "center" }}>Упс...</H2>
          <DangerText style={{ textAlign: "center" }}>
            Это неверная ссылка активации. <br />
            Пожалуйста, обратитесь в поддержку.
          </DangerText>
        </Container>
      </Column>
    );
  }

  if (!user) {
    return (
      <Column>
        <Container>
          <FloatingLogo />
          <H2 style={{ textAlign: "center" }}>Пользователь не найден</H2>
          <MediumText style={{ textAlign: "center" }}>
            Пожалуйста, обратитесь в поддержку.
          </MediumText>
        </Container>
      </Column>
    );
  }

  if (isActivated) {
    return (
      <Column>
        <Container>
          <FloatingLogo />
          <SuccessH2 style={{ textAlign: "center" }}>Успешно!</SuccessH2>
          <MediumText style={{ textAlign: "center" }}>
            Ваш аккаунт активирован. <br />
          </MediumText>
        </Container>
      </Column>
    );
  }

  return (
    <Column>
      <Container>
        <Toast ref={toastRef} position="bottom-center" />
        <FloatingLogo />
        <H2 style={{ textAlign: "center" }}>
          Добро пожаловать, <br />
          {user?.lastName} {user?.name}
        </H2>
        <InputFlex>
          <CustomInputText
            value={email}
            setValue={() => undefined}
            placeholder="Почта"
            icon={<IconMail size={20} />}
            disabled={true}
          />
        </InputFlex>
        <MediumText style={{ textAlign: "center" }}>
          Придумайте пароль для своего аккаунта
        </MediumText>
        <InputFlex>
          {getPasswordError() && <DangerText>{getPasswordError()}</DangerText>}
          <CustomPassword
            value={password.password}
            setValue={handleNewPasswordChange}
            placeholder="Новый пароль"
            invalid={!!getPasswordError()}
            onBlur={() => handleBlur("password")}
          />
        </InputFlex>
        <InputFlex>
          {getConfirmError() && <DangerText>{getConfirmError()}</DangerText>}
          <CustomPassword
            value={password.confirm}
            setValue={handleConfirmPasswordChange}
            placeholder="Подтвердите пароль"
            invalid={!!getConfirmError()}
            onBlur={() => handleBlur("confirm")}
          />
        </InputFlex>
        <Button label="Сохранить" onClick={handleSubmit} loading={loading} />
      </Container>
    </Column>
  );
};

export default ActivateUser;
